import { useEffect, useMemo, useRef, useState } from 'react';
import { MeasurementMarkerDto, PlayerOptionDto, SessionOptionDto, KIND_OPTIONS } from 'types/models/SessionsPageData';
import { t } from 'utilities/TranslationUtils';
import PlayerChip from './PlayerChip';
import SessionKindFilterPill from './SessionKindFilterPill';
import RecentSessionCard from './RecentSessionCard';
import { matchesQuery } from './playerNameMatch';
import {
  recentPlayers, clubTeamCells, playersForTeam, recentSessionCardsFromSessions, RecentPlayer, teamColor, KIND_FILTER,
  filterSessionCards,
} from './playerPerformanceUtils';

interface PlayerSelectionScreenProps {
  players: PlayerOptionDto[];
  /** Recent-session list from the org-basic snapshot — backs the recent-session cards (no measurements). */
  sessions: SessionOptionDto[];
  measurements: MeasurementMarkerDto[];
  teamFilterActive: boolean;
  selectedTeamKey: string | null;
  onToggleTeamFilter: (active: boolean) => void;
  onSelectTeam: (key: string) => void;
  onClearTeamFilter: () => void;
  onFocusPlayer: (playerId: string, sessionTypeFilter?: string) => void;
  /** Opens the session page for a session card, focused on that card's player. */
  onOpenSession: (sessionId: string, playerId: string) => void;
}

function kindLabel(kind: string): string {
  const opt = KIND_OPTIONS.find((k) => k.group === kind);
  return opt ? t(opt.labelKey) : kind;
}

function shortDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

const PlayerSelectionScreen: React.FunctionComponent<PlayerSelectionScreenProps> = ({
  players, sessions, measurements, teamFilterActive, selectedTeamKey,
  onToggleTeamFilter, onSelectTeam, onClearTeamFilter, onFocusPlayer, onOpenSession,
}) => {
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  // Session-card filter: session types (empty = all).
  const [kindFilter, setKindFilter] = useState<string[]>([]);

  const recent = useMemo(() => recentPlayers(players, measurements), [players, measurements]);
  const teamCells = useMemo(() => clubTeamCells(players, measurements), [players, measurements]);
  const teamPlayers = useMemo<RecentPlayer[]>(
    () => (selectedTeamKey ? playersForTeam(players, measurements, selectedTeamKey) : []),
    [players, measurements, selectedTeamKey],
  );
  const teamPlayerIds = useMemo<Set<string>>(
    () => new Set(teamPlayers.map((p) => p.id)),
    [teamPlayers],
  );

  const teamScopedCards = useMemo(() => {
    const cards = recentSessionCardsFromSessions(sessions, players);
    return selectedTeamKey ? cards.filter((c) => teamPlayerIds.has(c.playerId)) : cards;
  }, [sessions, players, selectedTeamKey, teamPlayerIds]);
  // The player search narrows the session cards to the matching players too.
  const searchedCards = useMemo(
    () => (query.trim() === '' ? teamScopedCards : teamScopedCards.filter((c) => matchesQuery(c.playerName, query))),
    [teamScopedCards, query],
  );
  const kindOptions = useMemo(
    () => Array.from(new Set(searchedCards.map((c) => c.kind))).map((kind) => ({ value: kind, label: kindLabel(kind) })),
    [searchedCards],
  );
  const sessionCards = useMemo(() => filterSessionCards(searchedCards, kindFilter), [searchedCards, kindFilter]);

  const sessionPlayerMeasurements = useMemo(() => {
    const map = new Map<string, MeasurementMarkerDto[]>();
    for (const m of measurements) {
      const key = `${m.sessionId}␟${m.playerId}`;
      const arr = map.get(key) ?? [];
      arr.push(m);
      map.set(key, arr);
    }
    return map;
  }, [measurements]);

  // The snapshot has no players at all — collapse both cards to a single centered empty message
  // and hide the controls (search / team filter / hints) that would have nothing to act on.
  const hasNoData = players.length === 0;
  const showTeamCells = teamFilterActive && !selectedTeamKey;
  const showTeamPlayers = teamFilterActive && !!selectedTeamKey;
  const showSearch = !showTeamCells && !hasNoData; // all-players OR team selected

  // Reset paging/search when the active list changes.
  useEffect(() => { setSearchOpen(false); setQuery(''); }, [teamFilterActive, selectedTeamKey]);
  useEffect(() => { if (searchOpen) inputRef.current?.focus(); }, [searchOpen]);

  const activeList = showTeamPlayers ? teamPlayers : recent;
  const searching = query.trim() !== '';
  const filtered = searching ? activeList.filter((p) => matchesQuery(p.name, query)) : activeList;

  const hint = showTeamCells
    ? t('playerPerformance.playersHintTeam')
    : showTeamPlayers
      ? t('playerPerformance.playersHintTeamPicked')
      : t('playerPerformance.playersHintRecent');

  const resetSearch = (): void => {
    setQuery(''); setSearchOpen(false);
    if (showTeamPlayers) onClearTeamFilter();
  };

  return (
    <div className='pp-selection-layout' data-test='pp-selection-screen'>
      <div className='pp-section pp-selection-players'>
        <div className='pp-section-head'>
          <h4>{t('playerPerformance.playersTitle')}</h4>
          <div className='pp-section-head__actions'>
            {showSearch && (
              <div className='pp-search'>
                {searchOpen ? (
                  <input
                    ref={inputRef}
                    className='pp-search-input'
                    data-test='pp-search-input'
                    placeholder={t('playerPerformance.searchPlaceholder')}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                  />
                ) : (
                  <button
                    type='button'
                    className='btn btn-transparent font-oswald font-13 font-500 text-uppercase'
                    data-test='pp-search-toggle'
                    onClick={() => setSearchOpen(true)}
                  >
                    {t('playerPerformance.search')}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
        {!hasNoData && <p className='pp-hint'>{hint}</p>}

        {showTeamPlayers && (
          <button type='button' className='pp-active-filter' onClick={onClearTeamFilter} data-test='pp-clear-team-filter'>
            <span>{selectedTeamKey?.replace('␟', ' - ')}</span>
            <span aria-hidden='true'>·&nbsp;✕</span>
            <span>{t('playerPerformance.clearFilter')}</span>
          </button>
        )}

        <div className={`pp-chips${showTeamCells ? '' : ' pp-chips--list'}`}>
          {!showTeamPlayers && !searching && !hasNoData && (
            <button
              type='button'
              className={`pp-filter-chip${teamFilterActive ? ' pp-filter-chip--active' : ''}`}
              onClick={() => onToggleTeamFilter(!teamFilterActive)}
              data-test='pp-filter-by-team'
            >
              {t('playerPerformance.filterByTeam')}
            </button>
          )}

          {showTeamCells && teamCells.map((cell) => {
            const color = teamColor(cell.teamName);
            return (
              <button
                key={cell.key}
                type='button'
                className='pp-team-cell'
                style={{ borderColor: color, background: `${color}22` }}
                onClick={() => onSelectTeam(cell.key)}
                data-test='pp-team-cell'
              >
                <span className='pp-team-filter-dot' style={{ background: color }} />
                {cell.clubName} - {cell.teamName}
              </button>
            );
          })}

          {!showTeamCells && filtered.map((p) => (
            <PlayerChip key={p.id} player={p} onClick={() => onFocusPlayer(p.id)} />
          ))}
        </div>

        {searching && filtered.length === 0 && (
          <div className='pp-no-results' data-test='pp-no-search-results'>
            <p className='pp-hint'>{t('playerPerformance.noSearchResults', { query })}</p>
            <button type='button' className='pp-show-all' data-test='pp-show-all' onClick={resetSearch}>
              {t('playerPerformance.showAllPlayers')}
            </button>
          </div>
        )}

        {hasNoData ? (
          <div className='pp-empty' data-test='pp-no-players'>{t('playerPerformance.noPlayers')}</div>
        ) : (
          !showTeamCells && !searching && activeList.length === 0 && (
            <p className='pp-hint' data-test='pp-no-players'>{t('playerPerformance.noPlayers')}</p>
          )
        )}
      </div>

      <div className='pp-section pp-selection-sessions'>
        <div className='pp-section-head'>
          <h4>{t('playerPerformance.recentSessionsTitle', { count: sessions.length })}</h4>
        </div>
        {!hasNoData && <p className='pp-hint'>{t('playerPerformance.recentSessionsHint')}</p>}
        {!hasNoData && (
          <div className='sessions-preview-controls pp-landing-filters' data-test='pp-session-filters'>
            <SessionKindFilterPill options={kindOptions} selected={kindFilter} onChange={setKindFilter} />
          </div>
        )}
        {sessionCards.length === 0 ? (
          <div className='pp-session-cards-empty' data-test='pp-no-sessions'>
            {selectedTeamKey
              ? t('playerPerformance.noSessionsForTeam', { teamName: selectedTeamKey.replace('␟', ' - ') })
              : t('playerPerformance.noSessions')}
          </div>
        ) : (
          <div className='pp-session-cards'>
            {sessionCards.map((card) => (
              <RecentSessionCard
                key={`${card.sessionId}-${card.playerId}`}
                card={card}
                kindLabel={kindLabel(card.kind)}
                countLabel={t('playerPerformance.kicks', { count: card.kickCount })}
                dateLabel={shortDate(card.capturedAtUtc)}
                measurements={sessionPlayerMeasurements.get(`${card.sessionId}␟${card.playerId}`) ?? []}
                onShowPerformance={() => onFocusPlayer(card.playerId, KIND_FILTER[card.kind].filterValue)}
                onOpenSession={() => onOpenSession(card.sessionId, card.playerId)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PlayerSelectionScreen;
